import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOpenOnNew } from "@/hooks/useOpenOnNew";
import { useTermsColumnsVisibleByDefault } from "@/entities/dataTables/currencyColumns";
import {
  DEALS_TERMS_COLUMN_IDS,
  excludeBuiltinOwnedDealFields,
  getDealsCurrencyGroupByOptions,
  getDealsHiddenByDefaultColumnIds,
  getDealsSortMenuExclusions,
  useDealsCurrencyColumnLabels
} from "@/pages/deals/components/DealsTable/DealsTable";
import Text from "@/components/ui/text";
import { Add, Table, Kanban } from "@dreamhub/designsystem/icons";
import { useTranslation } from "react-i18next";
import Tabs from "@/components/controls/Tabs";
import KanbanBoard from "@/pages/deals/components/KanbanBoard/KanbanBoard";
import DealsTable from "@/pages/deals/components/DealsTable/DealsTable";
import { useSearchParams } from "react-router-dom";
import DealsToolbar, {
  dealsFilterPickerExcludedKeys,
  useDealsFilterOptions,
  getDealsSortByOptions
} from "@/pages/deals/components/DealsToolbar";
import ToolbarActions from "@/components/controls/ToolbarActions/ToolbarActions";
import { EmptyFilters } from "@/services/api/types";
import { UserData } from "@/entities/users/types";
import CreateDealModal from "@/pages/deals/components/CreateDealModal";
import { useDealsMe, useResetDealsMe } from "@/pages/deals/hooks";
import { useFieldVisibility, useEntityFields } from "@/pages/settings/hooks";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import TopKPIBar from "@/components/controls/TopKPIBar";
import {
  EmptyView,
  ViewDataConfiguration,
  ViewType
} from "@/entities/views/types";
import { SortingState } from "@tanstack/react-table";
import {
  ColumnData,
  GROUP_BY_PARAM,
  SORT_BY_PARAM,
  SORT_DIR_PARAM,
  SortDirection
} from "@/entities/dataTables/types";
import {
  getDefaultColumnsStateWithCustomFields,
  mergeMissingDefaultColumns,
  getPageNameFromTableId,
  excludePartnerFields
} from "@/entities/dataTables/helpers";
import { useEnablePartnerships } from "@/pages/partners/hooks";
import { useAccountSettings } from "@/entities/settings/hooks";
import { KanbanProvider } from "@/pages/deals/components/KanbanBoard/KanbanProvider";
import {
  dealsColumnKeys,
  DEALS_SORT_FIELD_BY_COLUMN,
  canSeedDealColumns,
  normalizeDealsGroupBy,
  toDealsRequestFilters
} from "@/pages/deals/helpers";
import { DEALS_EXPORT_EXCLUDED_COLUMNS } from "@/pages/deals/types";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { IconButton } from "@/components/ui/icon-button";
import ExportMenu from "@/components/ui/export-menu";
import { isEqual } from "lodash";
import { useClosedLostReasons } from "@/entities/settings/deal/hooks";
import EntityLogoIconContainer from "@/components/ui/entity-logo-icon-container";
import { useEntityExport } from "@/services/hooks/entities/useEntityExport";
import { SCORE_CONFIGS, ScoreType } from "@/pages/deals/scoreConfig";
import { tWithFallback } from "@/utils/i18n";

// Excel export: each terms column is priced in the deal's own contract currency.
const DEALS_EXPORT_CURRENCY_FIELD_BY_COLUMN: Readonly<Record<string, string>> =
  Object.fromEntries(
    [...DEALS_TERMS_COLUMN_IDS].map((id) => [id, "contractsCurrency"])
  );

interface Props {
  userData: UserData;
}

const Layouts = ["board", "table"];
type LayoutType = (typeof Layouts)[number];

const TABLE_ID = "deals-table";

// Map raw SCO score values (e.g. "PENDING", "PASS") to their UI labels so the
// CSV export matches what's rendered by DealScoreBadge. Both upper- and lower-case
// raw forms are included because the backend value casing isn't guaranteed.
const SCORE_COLUMN_TYPES: Readonly<Record<string, ScoreType>> = {
  keyStakeholdersMappingText: ScoreType.STAKEHOLDERS_MAPPING,
  championsMappingText: ScoreType.CHAMPION_SCORE,
  qualificationText: ScoreType.QUALIFICATION,
  stakeholdersApprovalText: ScoreType.STAKEHOLDERS_APPROVAL,
  pocText: ScoreType.POC
};

const buildScoreColumnValueLabels = (): Record<
  string,
  Record<string, string>
> => {
  const result: Record<string, Record<string, string>> = {};
  for (const [colId, scoreType] of Object.entries(SCORE_COLUMN_TYPES)) {
    const config = SCORE_CONFIGS[scoreType];
    if (!config) continue;
    const labelMap: Record<string, string> = {};
    for (const [scoreKey, translationKey] of Object.entries(
      config.valueMapping
    )) {
      if (!translationKey) continue;
      const label = tWithFallback(
        `deals.kpis.${scoreType}Scores.${translationKey}`,
        scoreKey
      );
      labelMap[scoreKey.toUpperCase()] = label;
      labelMap[scoreKey.toLowerCase()] = label;
    }
    if (Object.keys(labelMap).length > 0) result[colId] = labelMap;
  }
  return result;
};

const DEAL_VIEW_PARAM = "view";
const DEAL_VIEW_STORAGE_KEY = "dealView";

const Deals: React.FC<Props> = ({ userData }) => {
  const { t } = useTranslation();
  const {
    fieldName: arrOrMrrField,
    label: arrOrMrrLabel,
    isResolved: arrMrrResolved
  } = useArrMrr();

  const [searchParams, setSearchParams] = useSearchParams();
  const [layout, setLayout] = useState<LayoutType>(() => {
    const urlParam = searchParams.get(DEAL_VIEW_PARAM);
    if (Layouts.includes(urlParam as LayoutType)) {
      return urlParam as LayoutType;
    }
    const stored = sessionStorage.getItem(DEAL_VIEW_STORAGE_KEY);
    if (Layouts.includes(stored as LayoutType)) {
      return stored as LayoutType;
    }
    return "board";
  });
  const [filters, setFilters] = useState(EmptyFilters);
  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, onSortingChange] = useState<SortingState>([
    {
      id: searchParams.get(SORT_BY_PARAM) || arrOrMrrField,
      desc:
        searchParams.get(SORT_DIR_PARAM) === SortDirection.ASC ? false : true
    }
  ]);
  const [viewReady, setViewReady] = useState(false);
  const handleViewActivated = useCallback(() => setViewReady(true), []);
  const [isCreateDealOpen, setIsCreateDealOpen] = useState(false);
  useOpenOnNew(setIsCreateDealOpen);
  const [resetPagination, setResetPagination] = useState(false);
  const [groupBy, setGroupByState] = useState<string[]>(() => {
    const groupByParam = searchParams.get(GROUP_BY_PARAM);
    return groupByParam ? normalizeDealsGroupBy(groupByParam.split(",")) : [];
  });
  // Every grouping — a link, a view being activated, the toolbar — passes through
  // here, so a replaced group option never reaches the table.
  const setGroupBy = useCallback(
    (next: string[]) => setGroupByState(normalizeDealsGroupBy(next)),
    []
  );
  const [columnsState, setColumnsState] = useState<ColumnData[]>([]);

  // totalRows is not threaded here because the row total lives inside
  // DealsTable's data hook; export falls back to the default 5-minute poll
  // budget. Wire totalRows through if large-deal exports start timing out.
  const { exportTo: exportDeals, isExporting: isExportingDeals } =
    useEntityExport({
      createRoute: "deals/export/create",
      statusRoute: "deals/export/status",
      tableName: "deals",
      filters: toDealsRequestFilters(filters),
      sorting,
      searchTerm,
      columnsState,
      getFilterOptions: () => dealsFilterOptions,
      excludeFromExport: DEALS_EXPORT_EXCLUDED_COLUMNS,
      // Mirrors the mapping DealsTable applies to its own sort request, so an
      // exported CSV comes out in the order shown on screen.
      sortFieldByColumn: DEALS_SORT_FIELD_BY_COLUMN,
      getExtraColumnValueLabels: buildScoreColumnValueLabels,
      columnCurrencyFields: DEALS_EXPORT_CURRENCY_FIELD_BY_COLUMN
    });

  const { data: customFieldsResponse } = useEntityFields(EntityType.DEAL);
  const enablePartnerships = useEnablePartnerships();
  const { isLoading: isAccountSettingsLoading } = useAccountSettings();
  const customFields = useMemo(() => {
    const fields = customFieldsResponse?.fields || [];
    return enablePartnerships ? fields : excludePartnerFields(fields);
  }, [customFieldsResponse, enablePartnerships]);
  // The picker builds its custom-column list from the registry; drop the rows a
  // built-in column already renders under a different id, or TCV/ACV each show
  // up twice.
  const columnManagementFields = useMemo(
    () => excludeBuiltinOwnedDealFields(customFields),
    [customFields]
  );
  const { filterVisibleColumnKeys } = useFieldVisibility(EntityType.DEAL);
  const {
    visibleByDefault: termsColumnsVisibleByDefault,
    isResolved: currencyUsageResolved
  } = useTermsColumnsVisibleByDefault();
  const currencyColumnLabels = useDealsCurrencyColumnLabels(
    arrOrMrrLabel,
    arrOrMrrField,
    customFields
  );
  const activeDealsColumnKeys = useMemo(
    () => filterVisibleColumnKeys(dealsColumnKeys),
    [filterVisibleColumnKeys]
  );
  const sortMenuExcludedColumnIds = useMemo(
    () => getDealsSortMenuExclusions(customFields),
    [customFields]
  );

  const defaultColumnsState = useMemo(
    () =>
      getDefaultColumnsStateWithCustomFields(
        activeDealsColumnKeys,
        columnManagementFields,
        getPageNameFromTableId(TABLE_ID),
        t,
        arrOrMrrLabel,
        arrOrMrrField,
        getDealsHiddenByDefaultColumnIds(termsColumnsVisibleByDefault),
        currencyColumnLabels,
        true
      ),
    [
      activeDealsColumnKeys,
      columnManagementFields,
      t,
      arrOrMrrLabel,
      arrOrMrrField,
      termsColumnsVisibleByDefault,
      currencyColumnLabels
    ]
  );

  // Opening a view saved without columns empties the layout, so the seeding
  // below re-runs on that too — under the same gates, never ahead of them.
  const isColumnsStateEmpty = columnsState.length === 0;
  useEffect(() => {
    // Wait for account settings so defaultColumnsState reflects the resolved
    // enablePartnerships flag before seeding — otherwise partner columns can
    // seed as excluded and never reappear once the flag resolves true.
    // Currency usage is gated on the same grounds: the terms columns default
    // to visible while it is in flight, and a seeded layout is never rewritten.
    if (
      !canSeedDealColumns({
        hasCustomFields: !!customFieldsResponse,
        isAccountSettingsLoading,
        currencyUsageResolved,
        arrMrrResolved
      })
    )
      return;
    // Seeding only fills an empty layout, so an account that saved one before
    // the split money columns existed would never be offered them. Merge under
    // the same gates for the same reason: a column folded in early is folded in
    // visible, and that guess survives the next save.
    setColumnsState((prev) =>
      prev.length === 0
        ? defaultColumnsState
        : mergeMissingDefaultColumns(prev, defaultColumnsState)
    );
  }, [
    defaultColumnsState,
    isColumnsStateEmpty,
    customFieldsResponse,
    isAccountSettingsLoading,
    currencyUsageResolved,
    arrMrrResolved
  ]);

  const { data: closedLostReasonsData } = useClosedLostReasons();
  const closedLostReasons = useMemo(
    () => closedLostReasonsData?.closedLostReasons || [],
    [closedLostReasonsData]
  );

  // Only use selectedView when in table layout
  const [selectedView, setSelectedView] = useState<ViewDataConfiguration>(
    EmptyView(ViewType.DEALS_TABLE)
  );

  // A callback function to handle column changes
  const handleColumnsChange = useCallback((updatedColumns?: ColumnData[]) => {
    if (updatedColumns) {
      setColumnsState(updatedColumns);
    }
  }, []);

  const { data: topKPIBarData } = useDealsMe();
  const resetDealsMe = useResetDealsMe();

  const handleChangeLayout = (newLayout: LayoutType) => {
    setLayout(newLayout);
    if (newLayout !== "table") {
      setSelectedView(EmptyView(ViewType.DEALS_TABLE));
    }
  };

  useEffect(() => {
    sessionStorage.setItem(DEAL_VIEW_STORAGE_KEY, layout);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set(DEAL_VIEW_PARAM, layout);

      if (layout !== "table" || groupBy.length == 0) {
        params.delete("groupBy");
      } else {
        params.set("groupBy", groupBy.join(","));
      }

      if (layout === "board") {
        if (sorting.length > 0) {
          params.set(SORT_BY_PARAM, sorting[0].id);
          params.set(
            SORT_DIR_PARAM,
            sorting[0].desc ? SortDirection.DESC : SortDirection.ASC
          );
        }
      } else {
        params.delete(SORT_BY_PARAM);
        params.delete(SORT_DIR_PARAM);
      }

      return params;
    });
  }, [layout, groupBy, sorting, setSearchParams]);

  const handleOpenCreateModal = () => {
    setIsCreateDealOpen(true);
  };

  const handleDealCreated = () => {
    setResetPagination(true);
  };

  const handlePaginationReset = () => {
    resetDealsMe();
    setResetPagination(false);
  };

  const currentViewType =
    layout === "table" ? ViewType.DEALS_TABLE : ViewType.DEALS_KANBAN;

  const dealsFilterOptions = useDealsFilterOptions({
    viewType: currentViewType,
    closedLostReasons,
    arrMrrFieldName: arrOrMrrField
  });

  const dealsSortByOptions = getDealsSortByOptions(arrOrMrrField);

  return (
    <div
      data-test="deals-page"
      className="relative flex flex-col max-h-[calc(var(--app-content-h)-var(--table-bottom-space))] h-full"
    >
      <div data-test="deals-header-section" className="flex flex-col">
        <div
          data-test="deals-header"
          className="flex justify-between p-4 pb-2 h-14"
        >
          <div data-test="deals-title" className="flex gap-[10px] items-center">
            <EntityLogoIconContainer entityType={EntityType.DEAL} />
            <Text as="div" variant="lgSubtitleH1" className="p-1">
              {t("deals.title")}
            </Text>
          </div>
          <div data-test="deals-actions" className="flex items-center gap-2">
            <Tabs
              data-test="deals-layout-tabs"
              options={{
                table: (
                  <div className="flex gap-[6px] items-center pr-1">
                    <Table />
                    <span>{t("deals.table")}</span>
                  </div>
                ),
                board: (
                  <div className="flex gap-[6px] items-center">
                    <Kanban />
                    <span>{t("deals.board")}</span>
                  </div>
                )
              }}
              defaultValue={layout}
              className="px-1 py-0.5"
              valueSelected={(value) => handleChangeLayout(value as LayoutType)}
            />
            {layout === "table" && (
              <ExportMenu
                label={t("export")}
                data-test="deals-export-button"
                onExport={exportDeals}
                isExporting={isExportingDeals}
              />
            )}
            <IconButton
              data-test="deals-create-button"
              onClick={handleOpenCreateModal}
              size="lg"
            >
              <Add />
            </IconButton>
          </div>
        </div>
        <TopKPIBar data-test="deals-top-kpi-bar" data={topKPIBarData} />
      </div>
      <DealsToolbar
        data-test="deals-toolbar"
        viewType={currentViewType}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        filters={filters}
        setFilters={setFilters}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        closedLostReasons={closedLostReasons}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        sorting={sorting}
        defaultColumnsState={defaultColumnsState}
        onSortingChange={onSortingChange}
        columnsState={columnsState}
        setColumnsState={setColumnsState}
        tableId={TABLE_ID}
        shouldShowEditColumns={layout === "table"}
        onColumnsChange={handleColumnsChange}
        customFields={customFields}
        onViewActivated={handleViewActivated}
      />
      <ToolbarActions
        data-test="deals-toolbar-actions"
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filters={filters}
        setFilters={setFilters}
        filterOptions={dealsFilterOptions}
        groupBy={groupBy}
        // The board has no rows to group.
        setGroupBy={layout === "table" ? setGroupBy : undefined}
        groupByOptions={getDealsCurrencyGroupByOptions(arrOrMrrField)}
        groupByDateBuckets
        sorting={sorting}
        onSortingChange={onSortingChange}
        sortByOptions={dealsSortByOptions}
        sortMenuExcludedColumnIds={sortMenuExcludedColumnIds}
        filterPickerExcludedKeys={dealsFilterPickerExcludedKeys}
        defaultSortByValue={arrOrMrrField}
        columnsState={columnsState}
        setColumnsState={setColumnsState}
        defaultColumnsState={defaultColumnsState}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        viewType={currentViewType}
        showTableColumns={layout === "table"}
        onColumnsChange={handleColumnsChange}
        tableId={TABLE_ID}
        customFields={customFields}
        className="p-2 pr-4"
      />
      {layout === "board" && (
        <KanbanProvider>
          <KanbanBoard
            data-test="deals-kanban-board"
            filters={filters}
            searchTerm={searchTerm}
            shouldDisableDragAndDrop={true}
            resetPagination={resetPagination}
            onPaginationReset={handlePaginationReset}
            sorting={sorting}
          />
        </KanbanProvider>
      )}
      {layout === "table" && (
        <DealsTable
          data-test="deals-table"
          filters={filters}
          setFilters={setFilters}
          filterOptions={dealsFilterOptions}
          searchTerm={searchTerm}
          resetPagination={resetPagination}
          onPaginationReset={handlePaginationReset}
          groupBy={groupBy}
          sorting={sorting}
          onSortingChange={onSortingChange}
          tableId={TABLE_ID}
          selectedView={selectedView}
          columnsState={columnsState}
          onColumnsChange={handleColumnsChange}
          customFields={customFields}
          viewReady={viewReady}
          hasActiveFilters={!isEqual(filters, EmptyFilters)}
          onClearFilters={() => setFilters(EmptyFilters)}
          onClearSearch={() => setSearchTerm("")}
          className="h-full"
        />
      )}
      <CreateDealModal
        data-test="deals-create-modal"
        isOpen={isCreateDealOpen}
        setIsOpen={setIsCreateDealOpen}
        userData={userData}
        onDealCreated={handleDealCreated}
      />
    </div>
  );
};

export default Deals;
